import React from 'react';
import { Table } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { IconGripVertical } from '@tabler/icons-react';

/**
 * Shared drag-and-drop priority reorder table.
 *
 * @param {object[]} items - rows to render, in their current (pre-drag) order
 * @param {object[]} columns - [{ key, label, headerClassName, className, render(item) }]
 * @param {string} droppableId - unique id for the Droppable region
 * @param {Function} onDragEnd - passed straight to DragDropContext
 * @param {(item) => string|number} [getRowKey] - defaults to item.id
 * @param {React.ReactNode} hint - text shown in the top info bar
 * @param {React.ReactNode} [rightContent] - extra controls in the top info bar (e.g. a sort-scope switch)
 * @param {boolean} [saving] - shows a spinner in the info bar while a reorder is being persisted
 */
export default function PriorityReorderTable({ items, columns, droppableId, onDragEnd, getRowKey = (item) => item.id, hint, rightContent, saving }) {
  return (
    <div style={{ border: '1px solid #e6e7e9', borderRadius: '4px', overflow: 'hidden' }}>
      <div className="px-3 py-2 d-flex align-items-center gap-2 border-bottom bg-azure-lt text-azure" style={{ fontSize: '0.82rem' }}>
        <IconGripVertical size={14} />
        <span className="me-auto">{hint}</span>
        {rightContent}
        {saving && <span className="spinner-border spinner-border-sm ms-2" role="status" />}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div style={{ maxHeight: '580px', overflowY: 'auto', overflowX: 'auto' }}>
          <Table className="table-vcenter table-hover mb-0">
            <thead style={{ position: 'sticky', top: 0, background: 'var(--tblr-bg-surface, white)', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
              <tr>
                <th style={{ width: '32px' }}></th>
                {columns.map((col) => <th key={col.key} className={col.headerClassName}>{col.label}</th>)}
              </tr>
            </thead>
            <Droppable droppableId={droppableId}>
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {items.map((item, index) => (
                    <Draggable key={getRowKey(item)} draggableId={String(getRowKey(item))} index={index}>
                      {(prov, snap) => (
                        <tr
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          style={{ ...prov.draggableProps.style, background: snap.isDragging ? 'var(--tblr-active-bg, #e8f0fe)' : undefined }}
                        >
                          <td {...prov.dragHandleProps} style={{ cursor: 'grab', color: 'var(--tblr-muted)', width: '32px' }}>
                            <IconGripVertical size={16} />
                          </td>
                          {columns.map((col) => (
                            <td key={col.key} className={col.className}>{col.render ? col.render(item) : item[col.key]}</td>
                          ))}
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </Table>
        </div>
      </DragDropContext>
    </div>
  );
}
